import { type ChatMessageType } from "@/api/chat";
import { useUser } from "@/api/user";
import { isToolResultCard, ToolCard } from "@/components/chats/chat-tool-cards";
import { BotIcon, CheckIcon, FileIcon, Undo2 } from "lucide-react";
import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const ChatMessage = ({
  message,
  setPdfViewerDoc,
  onEscalationConfirm,
  onUndoWidget,
}: {
  message: ChatMessageType;
  setPdfViewerDoc?: (doc: { id: string; filename: string }) => void;
  onEscalationConfirm?: (escalation: NonNullable<ChatMessageType["escalation"]>) => void;
  onUndoWidget?: (dashboardId: string, widgetId: string) => Promise<void> | void;
}) => {
  const user = useUser();
  const [escalationDismissed, setEscalationDismissed] = React.useState(false);
  const [isEscalating, setIsEscalating] = React.useState(false);
  const [undoState, setUndoState] = React.useState<"idle" | "pending" | "done">("idle");
  return (
    <div>
      <div
        className={`relative flex w-fit min-w-[60px] max-w-[80%] flex-col rounded-lg px-3 text-sm leading-6
          ${message.sender === "human" ? "whitespace-pre-wrap" : ""}
          ${message.sender !== "event" ? "py-2" : ""} ${
            message.sender === "event"
              ? "mt-[-10px]"
              : message.sender == "human"
                ? "bg-muted"
                : "bg-ai-chat text-ai-chat-foreground"
          } ${message.sender === "event" ? "mx-auto" : user.data?.id === message.user?.id ? "ml-auto" : "mr-auto"}`}
      >
        <div className="flex flex-row gap-1 text-xs">
          {message.sender === "event" ? (
            <>
              {message.content && !message.events ? (
                <span className="text-muted-foreground italic">{message.content}</span>
              ) : (
                [
                  { events: message.events?.context_added, action: "added" },
                  { events: message.events?.context_removed, action: "removed" },
                  { events: message.events?.user_added, action: "added" },
                  { events: message.events?.user_removed, action: "removed" },
                ].map(({ events, action }) =>
                  events?.map((ev, index) => (
                    <span
                      key={`${action}-${index}`}
                      className="text-muted-foreground"
                    >
                      <span className="font-semibold">
                        {message.user?.first_name} {message.user?.last_name}
                      </span>{" "}
                      {action} {ev.type}{" "}
                      <span className="font-semibold">{ev.item?.name}</span>
                    </span>
                  )),
                )
              )}
            </>
          ) : message.sender === "ai" ? (
            <>
              <BotIcon className="h-4 w-4 text-ai-chat-muted" />{" "}
              <span className="text-ai-chat-muted">ForgeIQ</span>
            </>
          ) : (
            <span className="text-muted-foreground">
              {message.user?.first_name
                ? `${message.user?.first_name} ${message.user?.last_name}`
                : message.user?.email}
            </span>
          )}
        </div>
        {message.object_context && (
          <div className="mb-1 flex items-center gap-1 text-xs opacity-80">
            <span className="rounded bg-white/20 px-1.5 py-0.5">
              {message.object_context.action || "Updating"}:{" "}
              {message.object_context.object_name}
            </span>
          </div>
        )}
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
            strong: ({ children }) => (
              <strong className="font-semibold">{children}</strong>
            ),
            ul: ({ children }) => (
              <ul className="mb-2 ml-4 list-disc space-y-1 last:mb-0">{children}</ul>
            ),
            ol: ({ children }) => (
              <ol className="mb-2 ml-4 list-decimal space-y-1 last:mb-0">{children}</ol>
            ),
            li: ({ children }) => <li className="pl-1">{children}</li>,
            h1: ({ children }) => (
              <h3 className="mb-1 mt-3 text-base font-bold first:mt-0">{children}</h3>
            ),
            h2: ({ children }) => (
              <h4 className="mb-1 mt-3 text-sm font-bold first:mt-0">{children}</h4>
            ),
            h3: ({ children }) => (
              <h5 className="mb-1 mt-2 text-sm font-semibold first:mt-0">{children}</h5>
            ),
            code: ({ children, className }) => {
              const isBlock = className?.includes("language-");
              if (isBlock)
                return (
                  <code className="mb-2 block rounded bg-black/5 p-2 text-xs last:mb-0">
                    {children}
                  </code>
                );
              return (
                <code className="rounded bg-black/5 px-1 py-0.5 text-xs">
                  {children}
                </code>
              );
            },
            pre: ({ children }) => <pre className="mb-2 last:mb-0">{children}</pre>,
            blockquote: ({ children }) => (
              <blockquote className="mb-2 border-l-2 border-current/20 pl-3 italic last:mb-0">
                {children}
              </blockquote>
            ),
            hr: () => <hr className="my-2 border-current/10" />,
            a: ({ href, children, ...props }) => {
              const linkClasses = "font-semibold hover:underline";
              if (href)
                return (
                  <Link to={href} className={linkClasses} {...props}>
                    {children}
                  </Link>
                );
            },
          }}
        >
          {message.content}
        </ReactMarkdown>
        {message.escalation && onEscalationConfirm && !escalationDismissed && (
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="h-7 bg-white/20 text-xs hover:bg-white/30"
              disabled={isEscalating}
              onClick={() => {
                setIsEscalating(true);
                onEscalationConfirm(message.escalation!);
                setEscalationDismissed(true);
              }}
            >
              {isEscalating ? "Sending..." : "Go ahead"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs opacity-70 hover:opacity-100"
              onClick={() => setEscalationDismissed(true)}
            >
              Never mind
            </Button>
          </div>
        )}
        {(() => {
          if (!message.tool_results?.length) return null;
          const cards = message.tool_results.filter(isToolResultCard);
          const textResults = message.tool_results.filter(
            (r) => !isToolResultCard(r),
          );
          return (
            <>
              {cards.map((card, index) => (
                <ToolCard key={`card-${index}`} card={card} />
              ))}
              {textResults.length > 0 && (
                <div className="mt-2">
                  <Accordion type="single" collapsible>
                    <AccordionItem
                      value="tool-results"
                      className="border-none"
                    >
                      <AccordionTrigger className="border-b py-2 pb-1 text-xs hover:no-underline">
                        Agent actions ({textResults.length})
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="mt-2 flex flex-col gap-2">
                          {textResults.map((result, index) => {
                            const displayText =
                              typeof result === "string"
                                ? result
                                : (
                                    result as {
                                      output?: string;
                                      error?: string;
                                    }
                                  )?.output ||
                                  (
                                    result as {
                                      output?: string;
                                      error?: string;
                                    }
                                  )?.error ||
                                  JSON.stringify(result);
                            return (
                              <div
                                key={`tool-result-${index}`}
                                className="flex items-center gap-x-1 text-xs"
                              >
                                <div>
                                  <CheckIcon className="w-4" />
                                </div>{" "}
                                {displayText}
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
            </>
          );
        })()}
        {message.sender === "ai" &&
          onUndoWidget &&
          undoState !== "done" &&
          message.object_context?.object_type === "widget" &&
          message.object_context?.object_id &&
          message.scope_dashboard_id &&
          message.cache_invalidations &&
          message.cache_invalidations.length > 0 && (
            <div className="mt-1.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 gap-1 px-2 text-xs opacity-70 hover:opacity-100"
                disabled={undoState === "pending"}
                onClick={async () => {
                  setUndoState("pending");
                  try {
                    await onUndoWidget(
                      message.scope_dashboard_id!,
                      message.object_context!.object_id,
                    );
                  } finally {
                    setUndoState("done");
                  }
                }}
              >
                <Undo2 className="h-3 w-3" />
                {undoState === "pending" ? "Undoing..." : "Undo"}
              </Button>
            </div>
          )}
      </div>
      {message.attachments?.length ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {message.attachments.map((attachment, index) => (
            <div
              key={`${attachment.filename}-${index}`}
              className="overflow-hidden rounded-md border border-muted-foreground/30"
            >
              <img
                src={`data:${attachment.mime_type};base64,${attachment.data}`}
                alt={attachment.filename}
                className="h-32 w-32 object-cover"
              />
            </div>
          ))}
        </div>
      ) : null}
      {message.sources?.length ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {message.sources.map((source) => (
            <div
              key={source.id}
              className="flex cursor-pointer gap-1 rounded-full bg-muted/90 p-2 text-xs text-muted-foreground hover:bg-muted/60"
              onClick={() => setPdfViewerDoc?.(source)}
            >
              <FileIcon className="h-4 w-4" /> {source.filename}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};
export default ChatMessage;
